import React from 'react';

import { Button, Group, Modal, ModalProps, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { DateValue, MonthPickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import dayjs from 'dayjs';

import { RevenueProjectionDateRangeFormValues } from './types';

interface SelectRevenueProjectionDateRangeModalProps extends Pick<ModalProps, 'opened' | 'onClose'> {
  setRevenueProjectionDateRangeFormValuesToRun: React.Dispatch<
    React.SetStateAction<RevenueProjectionDateRangeFormValues>
  >;
}

const SelectRevenueProjectionDateRangeModal = ({
  opened,
  onClose,
  setRevenueProjectionDateRangeFormValuesToRun,
}: SelectRevenueProjectionDateRangeModalProps): JSX.Element => {
  const theme = useMantineTheme();

  const form = useForm<RevenueProjectionDateRangeFormValues>({
    initialValues: {
      startDate: null,
      endDate: null,
    },
  });

  const handleClose = (): void => {
    form.reset();
    onClose();
  };

  const isRunProjectionDisabled = !form.values.startDate || !form.values.endDate;

  const currentMonth = dayjs().startOf('month').toDate();
  const maxDate = dayjs().add(11, 'months').startOf('month').toDate();

  const getMinEndDate = (): Date => {
    if (form.values.startDate) {
      return form.values.startDate;
    }
    return currentMonth;
  };

  const handleStartDateChange = (value: DateValue): void => {
    form.setFieldValue('startDate', value);
    // Reset end date if it's before the new start date
    if (form.values.endDate && value) {
      const isBeforeStartDate = dayjs(form.values.endDate).isBefore(dayjs(value));
      if (isBeforeStartDate) {
        form.setFieldValue('endDate', null);
      }
    }
  };

  const handleRunProjection = (): void => {
    setRevenueProjectionDateRangeFormValuesToRun(form.values);
    handleClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      size='xl'
      title={<Title order={4}>Select Future Dates</Title>}
      styles={{
        content: {
          // @ts-ignore
          overflowY: 'visible !important',
        },
        header: {
          borderRadius: theme.radius.md,
        },
      }}
      radius='md'
      zIndex={9999}
    >
      <Stack>
        <Text c='dimmed'>Select how far ahead you want to view projected revenue. (Up to 12 months)</Text>

        <Group grow noWrap my='lg'>
          <MonthPickerInput
            label='Begin Date'
            minDate={currentMonth}
            maxDate={maxDate}
            {...form.getInputProps('startDate')}
            onChange={handleStartDateChange}
          />

          <MonthPickerInput
            label='End Date'
            minDate={getMinEndDate()}
            maxDate={maxDate}
            disabled={!form.values.startDate}
            {...form.getInputProps('endDate')}
          />
        </Group>

        <Group position='right' spacing='md'>
          <Button variant='light' onClick={handleClose}>
            Cancel
          </Button>
          <Button disabled={isRunProjectionDisabled} onClick={handleRunProjection}>
            Run Projection
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default SelectRevenueProjectionDateRangeModal;
